import {
  ClerkProvider,
  OrganizationList,
  OrganizationProfile,
  OrganizationSwitcher,
  Show,
  SignIn,
  UserButton,
  UserProfile,
  useAuth,
  useOrganization,
  useUser,
} from "@clerk/react";
import "./styles.css";

function SupportImpersonationBanner() {
  const { actor, sessionId, signOut } = useAuth();
  const { user } = useUser();

  if (!actor) return null;

  const subject =
    user?.fullName
    || user?.primaryEmailAddress?.emailAddress
    || "este usuario";

  return (
    <aside className="support-impersonation-banner" role="status">
      <span className="support-impersonation-banner__dot" aria-hidden="true" />
      <span>
        <strong>Modo soporte</strong>
        {" · "}
        Estás usando la cuenta de {subject}. Todas las acciones quedan auditadas.
      </span>
      <button
        type="button"
        onClick={() => signOut({
          sessionId,
          redirectUrl: window.location.origin,
        })}
      >
        Finalizar sesión
      </button>
    </aside>
  );
}

export function TribunaleaAuthProvider({
  publishableKey,
  children,
  demoFallback,
  supportEmail = "soporte@tribunalea.com",
}) {
  if (!publishableKey) return demoFallback;

  return (
    <ClerkProvider
      publishableKey={publishableKey}
      signInFallbackRedirectUrl="/"
      signUpFallbackRedirectUrl="/"
      supportEmail={supportEmail}
    >
      <SupportImpersonationBanner />
      {children}
    </ClerkProvider>
  );
}

export function AuthPortal({
  productName,
  headline,
  description,
  proofPoints = [],
}) {
  return (
    <div className="auth-screen">
      <div className="auth-brand-panel">
        <span className="auth-eyebrow">TRIBUNALEA · {productName}</span>
        <h1>{headline}</h1>
        <p>{description}</p>
        <div className="auth-proof">
          {proofPoints.map((point) => (
            <span key={point}>{point}</span>
          ))}
        </div>
      </div>
      <div className="auth-form-panel">
        <SignIn
          routing="hash"
          withSignUp
          fallbackRedirectUrl="/"
          signUpFallbackRedirectUrl="/"
          appearance={{
            variables: {
              colorPrimary: "#15936f",
              colorText: "#1f302e",
              colorBackground: "#ffffff",
              borderRadius: "0.75rem",
            },
            elements: {
              cardBox: { boxShadow: "none" },
              card: {
                boxShadow: "0 18px 50px rgba(25, 52, 47, 0.10)",
                border: "1px solid #dde6e3",
              },
            },
          }}
        />
      </div>
    </div>
  );
}

export function AuthenticatedArea({ signedOut, children }) {
  return (
    <Show when="signed-in" fallback={signedOut}>
      {children}
    </Show>
  );
}

export function SuiteAccountControls() {
  return (
    <div className="clerk-account-controls">
      <OrganizationSwitcher
        hidePersonal={false}
        afterCreateOrganizationUrl="/"
        afterSelectOrganizationUrl="/"
      />
      <UserButton />
    </div>
  );
}

export function SuiteOrganizationProfile() {
  return <OrganizationProfile routing="hash" />;
}

export function SuiteTeamWorkspace() {
  const { isLoaded, organization } = useOrganization();

  if (!isLoaded) {
    return (
      <div className="auth-loading" role="status">
        Preparando tus espacios de trabajo…
      </div>
    );
  }

  if (organization) return <OrganizationProfile routing="hash" />;

  return (
    <div className="personal-workspace">
      <div className="personal-workspace-copy">
        <span className="auth-eyebrow">ESPACIO PERSONAL</span>
        <h2>Estás trabajando de forma independiente</h2>
        <p>
          Tus alertas son privadas. Si más adelante querés colaborar, podés crear
          un equipo o aceptar una invitación sin perder tu espacio personal.
        </p>
      </div>
      <OrganizationList
        hidePersonal={false}
        afterCreateOrganizationUrl="/equipo"
        afterSelectOrganizationUrl="/equipo"
      />
    </div>
  );
}

export function SuiteUserProfile() {
  return <UserProfile routing="hash" />;
}

export function useAccessToken() {
  const { getToken } = useAuth();
  return getToken;
}

export {
  OrganizationList,
  OrganizationProfile,
  OrganizationSwitcher,
  Show,
  SignIn,
  UserButton,
  UserProfile,
  useAuth,
  useOrganization,
  useUser,
};
